import { MedicalSpecialty, Physician, Place } from 'schema-dts';

import { PhysicianData } from '@/features/profile/profile.types';

import useFullName from './useFullName';

/**
 * Generates Physician schema for provider data
 *
 * See {@link https://schema.org/Physician} for schema documentation
 * @param {PhysicianData} data
 * @returns
 */
function usePhysicianSchema(data: PhysicianData): Physician {
  const fullName = useFullName({
    firstName: data?.physicianFirstName,
    middleInitial: data?.physicianMiddleInitial ?? undefined,
    lastName: data?.physicianLastName,
    designation: data?.physicianDesignation,
  });

  const physician: Physician = {
    '@type': 'Physician',
    knowsLanguage: data?.Languages ?? ['English'],
    image: data?.physicianPhotoUrl ?? '',
    name: fullName,
    description: data?.physicianBio ?? '',
    // Use only origin + pathname to avoid XSS via query/hash injection
    mainEntityOfPage:
      typeof window !== 'undefined'
        ? new URL(window.location.pathname, window.location.origin).href
        : '',
    location: data?.providerLocations
      ? data?.providerLocations.map(
          (location) =>
            ({
              address: {
                '@type': 'PostalAddress',
                streetAddress: location.street ?? '',
                addressLocality: location.city ?? '',
                addressRegion: location.state ?? '',
                postalCode: location.zip ? `${location.zip}` : '',
              },
              '@type': 'Place',
              hasMap: `https://www.google.com/maps?daddr=${location.latitude},${location.longitude}`,
              name: location.name ?? '',
              telephone: location.phone ?? '',
            }) as Place
        )
      : [],
    hospitalAffiliation: data?.affiliations
      ? data?.affiliations.map((affiliation) => {
          return {
            '@type': 'Hospital',
            name: affiliation.locationName,
          };
        })
      : [],
    medicalSpecialty: data?.providerSpecialties
      ? (data?.providerSpecialties.map(
          (specialty) => specialty.specialty
        ) as Array<MedicalSpecialty>)
      : [],
  };

  return physician;
}

export default usePhysicianSchema;
